import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Star, StarOff } from "lucide-react";

const DEFAULT_MODELS = {
    openai: "gpt-4o-mini",
    anthropic: "claude-3-5-sonnet-latest",
    deepseek: "deepseek-v4-pro",
    ollama: "llama3.1",
};
const DEFAULT_BASE_URL = {
    openai: "https://api.openai.com/v1",
    deepseek: "https://api.deepseek.com",
    ollama: "http://localhost:11434/v1",
};

export default function AdminProviders() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", provider: "deepseek", model: DEFAULT_MODELS.deepseek, api_key: "", base_url: DEFAULT_BASE_URL.deepseek, is_default: false, is_active: true });

    const load = () => api.get("/admin/providers").then((r) => setRows(r.data));
    useEffect(() => { load(); }, []);

    const changeProvider = (p) => setForm({ ...form, provider: p, model: DEFAULT_MODELS[p] || "", base_url: DEFAULT_BASE_URL[p] || "" });

    const create = async () => {
        try {
            const payload = { ...form, base_url: form.base_url || null };
            await api.post("/admin/providers", payload);
            toast.success("Provider added");
            setOpen(false);
            setForm({ name: "", provider: "deepseek", model: DEFAULT_MODELS.deepseek, api_key: "", base_url: DEFAULT_BASE_URL.deepseek, is_default: false, is_active: true });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const setDefault = async (id) => { await api.post(`/admin/providers/${id}/set-default`); toast.success("Default set"); load(); };
    const del = async (id) => { await api.delete(`/admin/providers/${id}`); toast.success("Removed"); load(); };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <div className="flex items-center justify-between">
                <div>
                    <span className="overline text-primary">▮ LLM Providers</span>
                    <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Pluggable providers</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Configure server-side OpenAI, Claude, DeepSeek, or Ollama credentials for Prepcheck.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="admin-add-provider-btn" className="rounded-sm bg-primary hover:bg-primary/90 btn-press">
                            <Plus className="w-4 h-4 mr-2" /> Add provider
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-sm">
                        <DialogHeader><DialogTitle>New LLM provider</DialogTitle></DialogHeader>
                        <div className="space-y-3 mt-2">
                            <div>
                                <Label className="overline">Name</Label>
                                <Input data-testid="provider-name-input" className="rounded-sm mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="DeepSeek prod" />
                            </div>
                            <div>
                                <Label className="overline">Provider</Label>
                                <Select value={form.provider} onValueChange={changeProvider}>
                                    <SelectTrigger data-testid="provider-provider-select" className="rounded-sm mt-2"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="openai">OpenAI</SelectItem>
                                        <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                                        <SelectItem value="deepseek">DeepSeek</SelectItem>
                                        <SelectItem value="ollama">Ollama</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline">Model</Label>
                                <Input data-testid="provider-model-input" className="rounded-sm mt-2 font-mono" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                            </div>
                            <div>
                                <Label className="overline">API key</Label>
                                <Input data-testid="provider-key-input" type="password" className="rounded-sm mt-2 font-mono" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="sk-…" />
                            </div>
                            {(form.provider === "deepseek" || form.provider === "openai" || form.provider === "ollama") && (
                                <div>
                                    <Label className="overline">Base URL (optional)</Label>
                                    <Input data-testid="provider-baseurl-input" className="rounded-sm mt-2 font-mono" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder={DEFAULT_BASE_URL[form.provider] || ""} />
                                </div>
                            )}
                            <label className="flex items-center gap-2">
                                <Switch data-testid="provider-default-switch" checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} />
                                <span className="text-sm">Set as default</span>
                            </label>
                            <Button data-testid="provider-save-btn" onClick={create} className="w-full rounded-sm bg-primary hover:bg-primary/90 btn-press">Save provider</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="mt-8 border border-border rounded-sm">
                <div className="grid grid-cols-12 gap-2 p-3 border-b border-border overline text-muted-foreground text-xs">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-2">Provider</div>
                    <div className="col-span-3">Model</div>
                    <div className="col-span-2">API Key</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No providers configured.</div>}
                {rows.map((p) => (
                    <div key={p.id} data-testid={`provider-row-${p.id}`} className="grid grid-cols-12 gap-2 p-3 border-b border-border items-center text-sm">
                        <div className="col-span-3 font-medium flex items-center gap-2">
                            {p.is_default && <Star className="w-4 h-4 text-primary fill-primary" />}
                            {p.name}
                        </div>
                        <div className="col-span-2 font-mono text-xs uppercase">{p.provider}</div>
                        <div className="col-span-3 font-mono text-xs">{p.model}</div>
                        <div className="col-span-2 font-mono text-xs text-muted-foreground">{p.api_key_masked}</div>
                        <div className="col-span-2 flex justify-end gap-1">
                            {!p.is_default && (
                                <Button data-testid={`provider-set-default-${p.id}`} size="sm" variant="ghost" onClick={() => setDefault(p.id)} className="rounded-sm">
                                    <StarOff className="w-4 h-4" />
                                </Button>
                            )}
                            <Button data-testid={`provider-delete-${p.id}`} size="sm" variant="ghost" onClick={() => del(p.id)} className="rounded-sm text-primary">
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
